import React, { useState } from 'react';
import { Button, Grid, Icon, Modal, Message, Segment, Loader } from 'semantic-ui-react';
import { numberToMoneyString } from '../../../../../../../services/masks';
import { FieldLabel } from '../../../../../../../styles';
import { post } from '../../../../../../../services/request';
import { dangerNotification, warningNotification } from '../../../../../../../services/notification';
import { useHistory } from 'react-router-dom';
import { InsurersCodeEnum } from '../../../../../../../enums/insurers-code.enum';
import FireInsuranceConditionsAcceptanceModal, {
  FireInsuranceConditionsAcceptance
} from '../../../../../components/FireInsuranceConditionsAcceptanceModal';
import { recordFireInsuranceConditionsAcceptance } from '../../../../../../../services/fire-insurance-conditions-acceptance';

// Interfaces locais
interface YelumApiResponse {
  quoteNo?: number;
  quoteYear?: number;
  proposalNo?: number;
  liquidPremium?: number;
  totalPremium?: number;
  monthlyPremium?: number;
  iof?: number;
  additionalPremium?: number;
  referenceCode?: string;
  fireInsuranceAnalysisId?: string;
  fireInsuranceId?: string;
  installments?: Array<{
    installmentNo: number;
    totalPremium: number;
  }>;
}

interface QuotationData {
  tenantName?: string;
  tenantDocument?: string;
  tenantCellphone?: string;
  beneficiaryName?: string;
  beneficiaryDocument?: string;
  propertyType?: string;
  rentValue?: number;
  propertyValue?: number;
  coverageType?: string;
  policyPeriodStart?: string;
  policyPeriodEnd?: string;
  assistance?: string;
  commissionTax?: number;
  // Campos de endereço
  propertyZipcode?: string;
  propertyStreet?: string;
  propertyNumber?: string;
  propertyComplement?: string;
  propertyDistrict?: string;
  propertyCity?: string;
  propertyState?: string;
  // Campos de cobertura adicional
  constructionType?: string;
  occupation?: string;
  historicalProtectedProperty?: boolean;
  sharedProperty?: boolean;
  coverages?: Array<{
    coverageName: string;
    value: number;
    totalPremium: number;
  }>;
}

interface YelumSummaryModalProps {
  open: boolean;
  onClose: () => void;
  data?: YelumApiResponse;
  previousData?: YelumApiResponse;
  quotationData?: QuotationData;
}

const ControlledField = ({
  label,
  value
}: {
  label: string;
  value: string | number | undefined;
}) => {
  return (
    <>
      <FieldLabel>{label}</FieldLabel>
      <span>{value}</span>
    </>
  );
};

const YelumSummaryModal: React.FC<YelumSummaryModalProps> = ({
  open,
  onClose,
  data,
  previousData,
  quotationData
}) => {
  const [loading, setLoading] = useState(false);
  const [acceptanceModalOpen, setAcceptanceModalOpen] = useState(false);
  const history = useHistory();
  // Verificar se houve mudança nos valores (comparando valores mensais da API)
  const hasValueChanged = () => {
    if (!previousData || !data) return false;

    const previousMonthly = previousData.monthlyPremium || 0;
    const currentMonthly = data.monthlyPremium || 0;

    // Considera mudança se a diferença for maior que R$ 0,10
    return Math.abs(previousMonthly - currentMonthly) > 0.1;
  };

  const valueChanged = hasValueChanged();
  const previousMonthly = previousData?.monthlyPremium || 0;
  const currentMonthly = data?.monthlyPremium || 0;

  const handleHire = async (
    acceptance?: FireInsuranceConditionsAcceptance
  ) => {
    if (!acceptance) {
      setAcceptanceModalOpen(true);
      return;
    }

    try {
      setLoading(true);
      if (!data?.fireInsuranceAnalysisId) {
        throw new Error('Identificador do orçamento não encontrado.');
      }
      await recordFireInsuranceConditionsAcceptance(
        data.fireInsuranceAnalysisId,
        acceptance.sendCertificateByEmail
      );
      const hireResponse = await post('/fire-insurance-proposals/hire/yelum', {
        fireInsuranceAnalysisId: data?.fireInsuranceAnalysisId
      });

      if (hireResponse?.pending) {
        warningNotification(
          'Contratação Solicitada',
          hireResponse.message || 'A contratação foi enfileirada e será processada quando o lote for regularizado.'
        );
        history.push('/admin/fire-insurance-budgets');
      } else {
        history.push('/admin/fire-insurance-policies');
      }
    } catch (error) {
      console.error('Erro ao contratar seguro:', error);
      dangerNotification('Erro ao contratar seguro:', (error as any).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      size="large"
      closeOnEscape={true}
      closeOnDimmerClick={true}
    >
      <Modal.Header>
        <Icon name="check circle" color="green" />
        Orçamento Salvo com Sucesso!
      </Modal.Header>

      <Loader active={loading} inline="centered" />

      <Modal.Content scrolling>
        {valueChanged && (
          <Message warning icon>
            <Icon name="exclamation triangle" />
            <Message.Content>
              <Message.Header>Atenção - Valor do Prêmio Alterado</Message.Header>
              <p>
                O valor final do prêmio foi ajustado pela seguradora.
                <br />
                <strong>Valor anterior:</strong> {numberToMoneyString(previousMonthly)}/mês
                <br />
                <strong>Valor atual:</strong> {numberToMoneyString(currentMonthly)}/mês
              </p>
            </Message.Content>
          </Message>
        )}

        <Message positive icon>
          <Icon name="shield" />
          <Message.Content>
            <Message.Header>Cotação Yelum Processada</Message.Header>
            <p>Cotação nº {data?.referenceCode}</p>
          </Message.Content>
        </Message>

        <Grid columns="equal" style={{ padding: '10px 0' }}>
          <Grid.Row>
            <Grid.Column>
              <Segment>
                <h4>
                  <Icon name="file text" />
                  Informações da Cotação
                </h4>
                <Grid columns={3}>
                  <Grid.Row>
                    <Grid.Column>
                      <ControlledField
                        label="Número da Cotação"
                        value={`${data?.quoteNo}/${data?.quoteYear}`}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <ControlledField
                        label="Código de Referência"
                        value={data?.referenceCode}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>
            </Grid.Column>
          </Grid.Row>

          {quotationData && (
            <>
              <Grid.Row>
                <Grid.Column>
                  <Segment>
                    <h4>
                      <Icon name="user" />
                      Dados do Segurado
                    </h4>
                    <Grid columns={3}>
                      <Grid.Row>
                        <Grid.Column>
                          <ControlledField
                            label="Nome"
                            value={quotationData.tenantName}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <ControlledField
                            label="Documento"
                            value={quotationData.tenantDocument}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <ControlledField
                            label="Celular"
                            value={quotationData.tenantCellphone}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      {quotationData.beneficiaryName && (
                        <Grid.Row>
                          <Grid.Column>
                            <ControlledField
                              label="Beneficiário"
                              value={quotationData.beneficiaryName}
                            />
                          </Grid.Column>
                          <Grid.Column>
                            <ControlledField
                              label="Documento do Beneficiário"
                              value={quotationData.beneficiaryDocument}
                            />
                          </Grid.Column>
                          <Grid.Column></Grid.Column>
                        </Grid.Row>
                      )}
                    </Grid>
                  </Segment>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Segment>
                    <h4>
                      <Icon name="home" />
                      Endereço do Imóvel
                    </h4>
                    <Grid columns={3}>
                      <Grid.Row>
                        <Grid.Column>
                          <ControlledField
                            label="CEP"
                            value={quotationData.propertyZipcode}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <ControlledField
                            label="Logradouro"
                            value={quotationData.propertyStreet}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <ControlledField
                            label="Número"
                            value={quotationData.propertyNumber}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column>
                          <ControlledField
                            label="Complemento"
                            value={quotationData.propertyComplement || 'Sem complemento'}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <ControlledField
                            label="Bairro"
                            value={quotationData.propertyDistrict}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <ControlledField
                            label="Cidade/UF"
                            value={
                              quotationData.propertyCity && quotationData.propertyState
                                ? `${quotationData.propertyCity} - ${quotationData.propertyState}`
                                : quotationData.propertyCity ||
                                quotationData.propertyState
                            }
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Segment>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column>
                  <Segment>
                    <h4>
                      <Icon name="shield alternate" />
                      Dados da Cobertura
                    </h4>
                    <Grid columns={3}>
                      <Grid.Row>
                        <Grid.Column>
                          <ControlledField
                            label="Tipo do Imóvel"
                            value={quotationData.propertyType}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <ControlledField
                            label="Tipo de Cobertura"
                            value={quotationData.coverageType}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <ControlledField
                            label="Assistência"
                            value={quotationData.assistance}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column>
                          <ControlledField
                            label="Valor do Aluguel"
                            value={
                              quotationData.rentValue
                                ? numberToMoneyString(quotationData.rentValue)
                                : 'Não informado'
                            }
                          />
                        </Grid.Column>
                      </Grid.Row>
                      {(quotationData.policyPeriodStart ||
                        quotationData.policyPeriodEnd) && (
                          <Grid.Row>
                            <Grid.Column>
                              <ControlledField
                                label="Início da Vigência"
                                value={
                                  quotationData.policyPeriodStart
                                    ? new Date(
                                      quotationData.policyPeriodStart
                                    ).toLocaleDateString('pt-BR')
                                    : 'Não informado'
                                }
                              />
                            </Grid.Column>
                            <Grid.Column>
                              <ControlledField
                                label="Fim da Vigência"
                                value={
                                  quotationData.policyPeriodEnd
                                    ? new Date(
                                      quotationData.policyPeriodEnd
                                    ).toLocaleDateString('pt-BR')
                                    : 'Não informado'
                                }
                              />
                            </Grid.Column>
                          </Grid.Row>
                        )}
                    </Grid>
                  </Segment>
                </Grid.Column>
              </Grid.Row>

              {quotationData.coverages && quotationData.coverages.length > 0 && (
                <Grid.Row>
                  <Grid.Column>
                    <Segment>
                      <h4>
                        <Icon name="list alternate" />
                        Detalhamento das Coberturas
                      </h4>
                      {quotationData.coverages.map((coverage, index) => (
                        <Grid columns={3} key={index} style={{ marginBottom: '10px' }}>
                          <Grid.Row>
                            <Grid.Column>
                              <ControlledField
                                label="Cobertura"
                                value={coverage.coverageName}
                              />
                            </Grid.Column>
                            <Grid.Column>
                              <ControlledField
                                label="Valor Segurado"
                                value={numberToMoneyString(coverage.value)}
                              />
                            </Grid.Column>
                            <Grid.Column>
                              <ControlledField
                                label="Prêmio"
                                value={numberToMoneyString(coverage.totalPremium)}
                              />
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      ))}
                    </Segment>
                  </Grid.Column>
                </Grid.Row>
              )}
            </>
          )}

          <Grid.Row>
            <Grid.Column>
              <Segment>
                <h4>
                  <Icon name="calculator" />
                  Detalhamento dos Valores
                </h4>
                <Grid columns={3}>
                  <Grid.Row>
                    <Grid.Column>
                      <ControlledField
                        label="Prêmio Líquido"
                        value={numberToMoneyString(data?.liquidPremium || 0)}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <ControlledField
                        label="IOF"
                        value={numberToMoneyString(data?.iof || 0)}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <ControlledField
                        label="Prêmio Mensal"
                        value={numberToMoneyString(currentMonthly)}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>

      <Modal.Actions>
        <Button primary icon labelPosition="right" onClick={onClose}>
          <Icon name="close" />
          Fechar
        </Button>
        <Button positive icon labelPosition="right" onClick={() => handleHire()}>
          <Icon name="checkmark" />
          Contratar
        </Button>
      </Modal.Actions>
    </Modal>
    <FireInsuranceConditionsAcceptanceModal
      open={acceptanceModalOpen}
      insurerCode={InsurersCodeEnum.Yelum}
      loading={loading}
      onCancel={() => setAcceptanceModalOpen(false)}
      onConfirm={acceptance => {
        setAcceptanceModalOpen(false);
        void handleHire(acceptance);
      }}
    />
    </>
  );
};

export default YelumSummaryModal;
